import { useEffect, useState } from "react";
import { Banner, Card, SectionTitle, formatBytes } from "../components/ui";
import type { Api } from "../lib/api";
import type { PortalDocument } from "../lib/types";

/**
 * Documents — the shared customer documents library. Every provisioned customer sees the
 * same published set (PDFs etc.), downloaded via short-lived signed URLs. Self-help
 * answers now live entirely on the FAQ tab, so this tab is purely the document library.
 */
export function Documents({ api }: { api: Api }) {
  const [documents, setDocuments] = useState<PortalDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    api
      .listDocuments()
      .then((r) => setDocuments(r.documents))
      .catch(() => setError("Could not load the documents library."));
  }, [api]);

  async function download(d: PortalDocument) {
    setBusyId(d.id);
    setError(null);
    try {
      const { url } = await api.documentDownloadUrl(d.id);
      window.location.assign(url);
    } catch {
      setError("Download was denied or the link could not be minted.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <SectionTitle
        kicker="Documents · Library"
        title="Documents"
        desc="Published guides, datasheets, and compliance documents for PumaMesh customers. Every download link is short-lived and audited."
      />

      {error && (
        <div className="mb-6">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {!documents && !error && <p className="text-sm text-ink-muted">Loading documents…</p>}
      {documents && documents.length === 0 && (
        <Card>
          <p className="text-sm text-ink-muted">No documents have been published yet.</p>
        </Card>
      )}

      <div className="grid gap-4">
        {documents?.map((d) => (
          <Card key={d.id}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold text-ink">{d.title}</span>
                  {d.category && <span className="badge-accent">{d.category}</span>}
                </div>
                {d.description && (
                  <p className="mt-1 text-sm text-ink-muted">{d.description}</p>
                )}
                <div className="mt-1 text-sm text-ink-dim">
                  {d.filename} · {formatBytes(d.sizeBytes)} ·{" "}
                  {new Date(d.publishedAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-stretch gap-2 md:items-end">
                <button
                  className="btn-primary"
                  disabled={busyId === d.id}
                  onClick={() => void download(d)}
                >
                  {busyId === d.id ? "Preparing…" : "Download"}
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
